// src/pages/StartChoice.jsx
// §10 de l'audit promesse : « Donnez-nous ce que vous avez » → le premier écran
// ne doit PAS être un formulaire à 25 champs, mais « Comment voulez-vous
// commencer ? » avec 4 chemins vers la première valeur.
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileText, Table2, Camera, PencilLine, ArrowRight, Lock } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { getOrCreateUnclassifiedCollection } from "../lib/unclassifiedCollection";
import UseMyPhoneModal from "../components/artwork/modals/UseMyPhoneModal";

// Même liste que SoftGate/ArtworkDetail.jsx (hasPaidPlanForAI) : seule la carte
// "documents" déclenche de l'OCR facturé (les 3 autres — inventaire, photo,
// manuel — ne touchent à aucun endpoint IA).
const AI_PLANS = ["premium", "art_advisor_premium", "family_office"];

// Même heuristique que le reste de l'app pour distinguer mobile/desktop : sur
// mobile la caméra native est directement accessible (pas besoin de QR).
const isMobileDevice = () =>
  /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
  (window.matchMedia && window.matchMedia("(pointer: coarse)").matches);

const COPY = {
  fr: {
    title: "Comment voulez-vous commencer ?",
    subtitle: "Vos œuvres sont déjà dans vos photos, factures et certificats. ARTKIV les transforme en collection structurée — vous validez, rien n'est inventé.",
    cards: {
      documents: { t: "J'ai des documents", d: "Factures, certificats, bordereaux, PDF. ARTKIV extrait et propose les fiches.", cta: "Importer des documents" },
      inventory: { t: "J'ai un inventaire", d: "Un fichier Excel ou CSV. Ne recommencez pas à zéro.", cta: "Importer mon inventaire" },
      photo: { t: "J'ai les œuvres devant moi", d: "Téléphone ou appareil photo. Commencez par l'image.", cta: "Photographier une œuvre" },
      manual: { t: "Je pars de zéro", d: "Créez une fiche à la main et complétez à votre rythme.", cta: "Créer une fiche" },
    },
    reassure: "ARTKIV prépare. Vous confirmez.",
    premiumBadge: "Premium", premiumHint: "Disponible avec le plan Premium (ou Advisor Premium / Family Office).",
    doneForYou: { label: "Ou laissez-nous le faire", t: "Vous préférez qu'on s'en occupe ?", d: "Déposez tout ce que vous avez — notre équipe structure votre collection et vous la remet prête à l'emploi.", cta: "Faites-le pour moi" },
    photoModal: { title: "Utiliser mon téléphone", subtitle: "Ou continuez ici, sur cet appareil.", desktopCta: "Prendre la photo avec mon téléphone", sending: "Création de la fiche…", error: "La photo n'a pas pu être enregistrée. Réessayez." },
  },
  en: {
    title: "How would you like to start?",
    subtitle: "Your artworks are already in your photos, invoices and certificates. ARTKIV turns them into a structured collection — you validate, nothing is invented.",
    cards: {
      documents: { t: "I have documents", d: "Invoices, certificates, auction slips, PDFs. ARTKIV extracts and proposes the records.", cta: "Import documents" },
      inventory: { t: "I have an inventory", d: "An Excel or CSV file. Don't start from scratch.", cta: "Import my inventory" },
      photo: { t: "I have the artworks in front of me", d: "Phone or camera. Start from the image.", cta: "Photograph an artwork" },
      manual: { t: "I'm starting from scratch", d: "Create a record by hand and complete it at your pace.", cta: "Create a record" },
    },
    reassure: "ARTKIV prepares. You confirm.",
    premiumBadge: "Premium", premiumHint: "Available with the Premium plan (or Advisor Premium / Family Office).",
    doneForYou: { label: "Or let us do it", t: "Prefer we handle it?", d: "Drop everything you have — our team structures your collection and hands it back ready to use.", cta: "Do it for me" },
    photoModal: { title: "Use my phone", subtitle: "Or continue here, on this device.", desktopCta: "Take the photo with my phone", sending: "Creating the record…", error: "The photo could not be saved. Please try again." },
  },
  de: {
    title: "Wie möchten Sie beginnen?",
    subtitle: "Ihre Werke stecken bereits in Ihren Fotos, Rechnungen und Zertifikaten. ARTKIV macht daraus eine strukturierte Sammlung — Sie bestätigen, nichts wird erfunden.",
    cards: {
      documents: { t: "Ich habe Dokumente", d: "Rechnungen, Zertifikate, Protokolle, PDFs. ARTKIV extrahiert und schlägt die Akten vor.", cta: "Dokumente importieren" },
      inventory: { t: "Ich habe ein Inventar", d: "Eine Excel- oder CSV-Datei. Nicht bei Null anfangen.", cta: "Inventar importieren" },
      photo: { t: "Ich habe die Werke vor mir", d: "Handy oder Kamera. Mit dem Bild beginnen.", cta: "Werk fotografieren" },
      manual: { t: "Ich beginne bei Null", d: "Akte manuell anlegen und in Ihrem Tempo ergänzen.", cta: "Akte erstellen" },
    },
    reassure: "ARTKIV bereitet vor. Sie bestätigen.",
    premiumBadge: "Premium", premiumHint: "Verfügbar mit dem Premium-Plan (oder Advisor Premium / Family Office).",
    doneForYou: { label: "Oder wir übernehmen", t: "Lieber, dass wir uns kümmern?", d: "Laden Sie alles hoch — unser Team strukturiert Ihre Sammlung und übergibt sie einsatzbereit.", cta: "Für mich erledigen" },
    photoModal: { title: "Mein Handy benutzen", subtitle: "Oder machen Sie hier auf diesem Gerät weiter.", desktopCta: "Foto mit meinem Handy aufnehmen", sending: "Akte wird erstellt…", error: "Das Foto konnte nicht gespeichert werden. Bitte erneut versuchen." },
  },
};
const pick = (l) => COPY[String(l || "fr").slice(0, 2).toLowerCase()] || COPY.fr;

const CARDS = [
  { key: "documents", icon: FileText, route: "/import-batch", requiresAI: true },
  { key: "inventory", icon: Table2, route: "/collections", requiresAI: false },
  { key: "photo", icon: Camera, route: null, requiresAI: false },
  { key: "manual", icon: PencilLine, route: "/add", requiresAI: false },
];

export default function StartChoice() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const tx = pick(i18n.resolvedLanguage || i18n.language);
  const { profile } = useAuth() || {};
  const hasPaidPlanForAI = AI_PLANS.includes(String(profile?.plan || "standard").trim().toLowerCase());

  // Carte « J'ai les œuvres devant moi » : sur mobile, l'appareil A la caméra
  // → capture native directe et création d'une pré-fiche. Sur desktop, pas de
  // caméra utile → on propose le pont QR « utiliser mon téléphone » au lieu
  // de renvoyer vers le formulaire complet (c'était le bug : tout le monde
  // atterrissait sur /add, y compris quelqu'un venu pour prendre une photo).
  const fileInputRef = useRef(null);
  const [showPhoneQr, setShowPhoneQr] = useState(false);
  const [photoStatus, setPhotoStatus] = useState("idle"); // idle | sending | error

  const handlePhotoCard = () => {
    if (isMobileDevice()) {
      fileInputRef.current?.click();
    } else {
      setShowPhoneQr(true);
    }
  };

  const handlePhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoStatus("sending");
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("not_authenticated");
      const collectionId = await getOrCreateUnclassifiedCollection(user.id);

      const clean = file.name.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-zA-Z0-9.\-_]/g, "_");
      const fileName = `${uuidv4()}-${clean}`;
      const { error: upErr } = await supabase.storage.from("artworks-images").upload(fileName, file, {
        cacheControl: "3600", upsert: false, contentType: file.type || "image/jpeg",
      });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("artworks-images").getPublicUrl(fileName);
      const url = pub?.publicUrl;
      const img = {
        id: `img_${Date.now()}_${uuidv4().slice(0, 8)}`, url, path: url, publicUrl: url,
        name: file.name, size: file.size, type: file.type, isPrimary: true,
        kind: "main", uploadedAt: new Date().toISOString(),
      };
      const { data: created, error: insErr } = await supabase.from("artworks")
        .insert({
          client_id: user.id,
          collection_id: collectionId,
          title: file.name.replace(/\.[^.]+$/, "") || "Nouvelle œuvre",
          images: [img],
          image_url: url,
          is_draft: true,
        })
        .select("id")
        .single();
      if (insErr) throw insErr;
      navigate(`/artwork/${created.id}/edit`);
    } catch (err) {
      console.error("StartChoice photo capture failed:", err);
      setPhotoStatus("error");
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#faf8f4", padding: "48px 20px", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 32, fontWeight: 400, color: "#1a1a1a", margin: 0, letterSpacing: "-0.02em" }}>{tx.title}</h1>
        <p style={{ fontSize: 15, color: "#6b6b6b", lineHeight: 1.65, margin: "10px 0 28px", maxWidth: 640 }}>{tx.subtitle}</p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(340px, 100%), 1fr))", gap: 16 }}>
          {CARDS.map(({ key, icon: Icon, route, requiresAI }) => {
            const c = tx.cards[key];
            const locked = requiresAI && !hasPaidPlanForAI;
            return (
              <button key={key} type="button"
                onClick={() => {
                  if (locked) { navigate("/pricing"); return; }
                  if (key === "photo") handlePhotoCard(); else navigate(route);
                }}
                title={locked ? tx.premiumHint : undefined}
                style={{ textAlign: "left", background: locked ? "#faf9f6" : "#fff", border: `1px solid ${locked ? "#ece7dc" : "#e5e3df"}`, borderRadius: 12, padding: 22, cursor: "pointer", display: "flex", flexDirection: "column", gap: 10, transition: "border-color 0.15s, box-shadow 0.15s", position: "relative", opacity: locked ? 0.7 : 1 }}
                onMouseEnter={(e) => { if (!locked) { e.currentTarget.style.borderColor = "#c9a961"; e.currentTarget.style.boxShadow = "0 2px 10px rgba(26,26,26,0.05)"; } }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = locked ? "#ece7dc" : "#e5e3df"; e.currentTarget.style.boxShadow = "none"; }}>
                {locked && (
                  <span style={{ position: "absolute", top: 14, right: 14, display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 999, background: "#f1ede2", color: "#8b7355", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    <Lock size={11} /> {tx.premiumBadge}
                  </span>
                )}
                <span style={{ width: 40, height: 40, borderRadius: 9, background: locked ? "#efece3" : "#faf6ea", color: "#8b7355", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={20} /></span>
                <span style={{ fontFamily: "Georgia, serif", fontSize: 19, color: "#1a1a1a" }}>{c.t}</span>
                <span style={{ fontSize: 13.5, color: "#6b6b6b", lineHeight: 1.55, flex: 1 }}>{locked ? tx.premiumHint : c.d}</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5, fontWeight: 600, color: locked ? "#8b7355" : "#1a1a1a", marginTop: 4 }}>
                  {locked ? tx.premiumBadge : c.cta} <ArrowRight size={15} />
                </span>
              </button>
            );
          })}
        </div>

        {/* Alternative « tout fait » : le service d'import assisté (funnel existant). */}
        <button type="button" onClick={() => navigate("/onboarding/deposit")}
          style={{ width: "100%", textAlign: "left", marginTop: 16, background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 12, padding: "20px 22px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.09em", color: "#c9a961", fontWeight: 700, marginBottom: 4 }}>{tx.doneForYou.label}</span>
            <span style={{ display: "block", fontFamily: "Georgia, serif", fontSize: 19 }}>{tx.doneForYou.t}</span>
            <span style={{ display: "block", fontSize: 13.5, color: "#d6d0c4", lineHeight: 1.55, marginTop: 4, maxWidth: 560 }}>{tx.doneForYou.d}</span>
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 16px", background: "#c9a961", color: "#1a1a1a", borderRadius: 8, fontSize: 13.5, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>{tx.doneForYou.cta} <ArrowRight size={15} /></span>
        </button>

        <p style={{ marginTop: 22, fontSize: 13, color: "#9ca3af", fontStyle: "italic" }}>{tx.reassure}</p>

        {/* Capture native (mobile) : déclenchée par handlePhotoCard, invisible sinon. */}
        <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoFile} style={{ display: "none" }} />

        {photoStatus === "sending" && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(250,248,244,0.9)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #e5e0d8", borderTopColor: "#c9a961", animation: "spin 0.8s linear infinite" }} />
              <span style={{ fontSize: 14, color: "#6b6b6b" }}>{tx.photoModal.sending}</span>
            </div>
            <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
          </div>
        )}
        {photoStatus === "error" && (
          <p style={{ marginTop: 12, fontSize: 13.5, color: "#b3261e" }}>{tx.photoModal.error}</p>
        )}

        <UseMyPhoneModal
          open={showPhoneQr}
          onClose={() => setShowPhoneQr(false)}
          actionType="new_artwork"
          onCompleted={(row) => {
            setShowPhoneQr(false);
            if (row?.result_path) navigate(`/artwork/${row.result_path}/edit`);
          }}
        />
      </div>
    </div>
  );
}
